import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet } from 'react-native';
import Collapsible from 'react-native-collapsible';
import { useSelector } from 'react-redux';
import {
  Layout,
  Select,
  SelectItem,
  Text,
  useTheme,
} from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../Config/constants';
import { useGetOrderDatesQuery } from '../../RTK/api/store';
import { storeCheckoutSelector } from '../../RTK/checkout/selectors';
import { checkout } from '../../RTK/defaultValues';
import {
  tagsFilterSelector,
  whenFilterSelector,
} from '../../RTK/filter/selectors';
import { selectPage } from '../../RTK/utility/selectors';
import ThemeColor from '../../Theme/colors';
import ThemeStyle from '../../Theme/styles';
import { AvailableOrderDates, StoreAvailableOrderDates } from '../../Types';

import Button from '../Button';
import PopupModal from '../Web/PopupModal';

import useSchedulePicker from './index.controller';
import { SchedulePickerProps } from './types';

const {
  DATE_ONLY_DISPLAY_FORMAT,
  TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
  DBDATE_FORMAT,
  ORDER_TYPES,
} = constants;

// This component is same on index.web.js BUT it's not generating the store hours
const SchedulePicker = ({
  storeId, // if this is pass, scheduleList is not needed
  scheduleList = [], // if this is pass, storeId is not needed
  scheduleTitle = 'Select Schedule',
  show: isOpen,
  onConfirm,
  onCancel,
}: SchedulePickerProps) => {
  const storeCheckoutData = useSelector((state) =>
    //@ts-ignore
    storeCheckoutSelector(state, storeId),
  );
  const filterHomeTags = useSelector(tagsFilterSelector);
  const whenFilter = useSelector(whenFilterSelector);

  const page = useSelector(selectPage);
  const {
    data: orderDatesData,
    isFetching: isFetchingOrderDates,
    isSuccess: isSuccessOrderDates,
  } = useGetOrderDatesQuery({ payload: storeId }, { skip: !Boolean(storeId) });

  const { generateDateOptions } = useSchedulePicker();

  const [selectedDate, setSelectedDate] = useState<any>();
  const [selectedHours, setSelectedHours] = useState<any>();

  const dates: StoreAvailableOrderDates[] = Boolean(storeId)
    ? isSuccessOrderDates
      ? orderDatesData
      : []
    : scheduleList.length > 0 || generateDateOptions();

  const canAsap =
    !Boolean(storeId) ||
    dates?.[0]?.time_options?.[0].value === 'asap_order_time' ||
    false;

  const [isAsap, setIsAsap] = useState<boolean>(lodashIsEmpty(whenFilter));

  useEffect(() => {
    if (!canAsap && isSuccessOrderDates) {
      setIsAsap(canAsap);
    }
  }, [canAsap, isSuccessOrderDates]);

  const selectedDayTimeSlot: AvailableOrderDates = lodashFind(dates, {
    value: dayjs(whenFilter?.value).format(DBDATE_FORMAT),
  });

  const hours = lodashFilter(
    selectedDate?.time_options || selectedDayTimeSlot?.time_options,
    (e) => e?.value !== 'asap_order_time',
  );
  const orderType = page.isHome
    ? filterHomeTags.filter((item) => item.paramName === 'order_type') // Filter for home page
    : storeCheckoutData[checkout.keys.ORDER_TYPE]; // for store page

  const isPickup = page.isHome
    ? orderType[0]?.values[0]?.value?.includes(ORDER_TYPES.PICKUP)
    : orderType?.value?.includes(ORDER_TYPES.PICKUP);

  const label = `${isPickup ? 'Pickup' : 'Delivery'}`;

  const orderTypeLabel = page.isHome
    ? orderType.length > 0
      ? label
      : 'Order'
    : label;

  const _formatDate = (data) => {
    if (lodashIsEmpty(data)) return undefined;
    const { iso } = data;
    return dayjs(iso).format(DATE_ONLY_DISPLAY_FORMAT);
  };

  const _formatTime = (data) => {
    if (lodashIsEmpty(data)) return undefined;
    const { start, end } = data;
    return `${dayjs(start).format(TIME_ONLY_DISPLAY_CAPITAL_FORMAT)} - ${dayjs(
      end,
    ).format(TIME_ONLY_DISPLAY_CAPITAL_FORMAT)}`;
  };

  const _onClose = () => {
    setSelectedDate(undefined);
    setSelectedHours(undefined);
    onCancel?.();
  };
  const toggleOrderTime = () => {
    setIsAsap(!isAsap);
    setSelectedDate(undefined);
    setSelectedHours(undefined);
  };

  const _onProceed = () => {
    if (isAsap) {
      onConfirm?.(null);
      _onClose();
      return;
    }
    const payload = !lodashIsEmpty(selectedHours)
      ? selectedHours.start
      : whenFilter?.value;

    return onConfirm?.(payload);
  };

  const store_theme = useTheme();
  const enabledStyle = {
    ...styles.enableBtn,
    backgroundColor: store_theme['button_color'] || ThemeColor.green,
    borderColor: store_theme['button_color'] || ThemeColor.green,
  };
  const disableProceedBtn = isAsap
    ? false
    : !lodashIsEmpty(selectedDate)
    ? lodashIsEmpty(selectedHours)
    : lodashIsEmpty(whenFilter?.value);

  return (
    <PopupModal
      isOpen={isOpen}
      onCloseButtonClick={_onClose}
      title={scheduleTitle}
      hasHeaderLine
    >
      <Layout
        style={[ThemeStyle.spacingTopSmall, ThemeStyle.spacingBottomSmall]}
      >
        {canAsap ? (
          <Layout style={ThemeStyle.spacingBottomMedium}>
            <Layout style={styles.container}>
              <Layout style={styles.layout} level="1">
                <Button
                  style={isAsap ? enabledStyle : styles.disableBtn}
                  onPress={toggleOrderTime}
                  plain
                >
                  <Layout style={styles.innerBtn}>
                    <Text category="p2" status={isAsap && 'control'}>
                      ASAP
                    </Text>
                  </Layout>
                </Button>
              </Layout>

              <Layout style={styles.layout} level="1">
                <Button
                  plain
                  style={!isAsap ? enabledStyle : styles.disableBtn}
                  onPress={toggleOrderTime}
                >
                  <Layout style={styles.innerBtn}>
                    <Text category="p2" status={!isAsap && 'control'}>
                      Pre-Order
                    </Text>
                  </Layout>
                </Button>
              </Layout>
            </Layout>
          </Layout>
        ) : (
          isFetchingOrderDates && (
            <ActivityIndicator color={store_theme['color-success-500']} />
          )
        )}

        <Collapsible collapsed={isAsap}>
          <Layout
            style={[ThemeStyle.spacingTopSmall, ThemeStyle.spacingBottomMedium]}
          >
            <Layout style={styles.container}>
              <Layout style={styles.layout} level="1">
                <Layout style={ThemeStyle.spacingBottomSmall} level="1">
                  <Text category="label">{orderTypeLabel} Date</Text>
                </Layout>
                <Select
                  accessoryRight={
                    isFetchingOrderDates && (
                      <ActivityIndicator
                        color={store_theme['color-success-500']}
                      />
                    )
                  }
                  placeholder={
                    isFetchingOrderDates ? 'Loading...' : 'Select Dates'
                  }
                  onSelect={({ row }: any) => {
                    let selectRow = row;
                    if (canAsap && dates[0].time_options.length === 1) {
                      selectRow += 1;
                    }
                    setSelectedDate(dates[selectRow]);
                    setSelectedHours(undefined);
                  }}
                  value={
                    !lodashIsEmpty(selectedDate)
                      ? _formatDate(selectedDate)
                      : !lodashIsEmpty(whenFilter?.value)
                      ? dayjs(whenFilter?.value).format(
                          DATE_ONLY_DISPLAY_FORMAT,
                        )
                      : ''
                  }
                  size="large"
                >
                  {dates.map(
                    (data, i) =>
                      canAsap &&
                      data.time_options.length > 1 && (
                        <SelectItem
                          key={i}
                          title={_formatDate(data)}
                          style={{ fontSize: 14 }}
                        />
                      ),
                  )}
                </Select>
              </Layout>

              <Layout style={styles.layout} level="1">
                <Layout style={ThemeStyle.spacingBottomSmall} level="1">
                  <Text category="label">
                    {orderTypeLabel === 'Delivery'
                      ? 'Dispatch'
                      : orderTypeLabel}{' '}
                    Time
                  </Text>
                </Layout>
                <Select
                  accessoryRight={
                    isFetchingOrderDates && (
                      <ActivityIndicator
                        color={store_theme['color-success-500']}
                      />
                    )
                  }
                  placeholder={
                    isFetchingOrderDates ? 'Loading...' : 'Select Hours'
                  }
                  disabled={
                    lodashIsEmpty(whenFilter?.value) &&
                    lodashIsEmpty(selectedDate)
                  }
                  onSelect={({ row }: any) => {
                    if (lodashIsEmpty(selectedDate)) {
                      const selectedDateTimeSlot = lodashFind(dates, {
                        value: dayjs(whenFilter?.value).format(DBDATE_FORMAT),
                      });
                      setSelectedDate({ iso: selectedDateTimeSlot?.iso });
                    }
                    setSelectedHours(hours[row]);
                  }}
                  value={
                    !lodashIsEmpty(selectedDate)
                      ? _formatTime(selectedHours)
                      : !lodashIsEmpty(whenFilter?.value)
                      ? _formatTime({
                          start: whenFilter?.value,
                          end: `${dayjs(whenFilter?.time, 'hh:mm')
                            .add(30, 'minutes')
                            .format()}`,
                        })
                      : ''
                  }
                  size="large"
                >
                  {hours.map((data, i) => (
                    <SelectItem key={i} title={_formatTime(data)} />
                  ))}
                </Select>
              </Layout>
            </Layout>
          </Layout>
        </Collapsible>

        <Button
          disabled={disableProceedBtn}
          plain
          style={[
            styles.proceedBtn,
            {
              borderColor: store_theme['button_color'] || ThemeColor.green,
            },
          ]}
          onPress={_onProceed}
        >
          <Text category="p2" status="basic">
            Proceed
          </Text>
        </Button>
      </Layout>
    </PopupModal>
  );
};

const styles = StyleSheet.create({
  container: {
    ...ThemeStyle.flex1,
    ...ThemeStyle.flexDirectionRowCenterSpaceBetween,
  },
  layout: {
    width: '48%',
  },
  button: {
    ...ThemeStyle.flex1,
    backgroundColor: ThemeColor.gray,
    marginTop: 10,
  },
  enableBtn: {
    flex: 1,
    marginTop: 10,
    borderWidth: 1,
  },
  disableBtn: {
    backgroundColor: ThemeColor.gray,
    flex: 1,
    marginTop: 10,
    opacity: 0.5,
    elevation: 0,
  },
  buttonActive: {
    backgroundColor: ThemeColor.green,
  },
  innerBtn: {
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  proceedBtn: {
    ...ThemeStyle.spacingTopMedium,
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
});

export default SchedulePicker;
